import { useEffect, useRef, useState } from 'react';
import { viewAsProId } from '@/lib/viewAs';
import { CELL_DOOR_OFF, ownerHubOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { listenForDesignerTokenRequests, openedStudios } from '@/lib/designer-token-broker';

/**
 * THE DESIGNER's door (UNIFY-1 — Peter's law, 2026-08-29): "it is one designer... the same
 * designer I use on the owners, the same designer I use on the pro side... I don't want to
 * see a different UI over on the pro side versus the owner side. The only different that I
 * wanna see is the guardrails."
 *
 * So this card no longer IS a designer — the interview, the editor, the looks, the photos
 * and every future idea live in THE ONE surface at imageos.co/pro/studio, which mounts the
 * exact components the owner uses, fed by the pro deployment. This card is the DOOR: it
 * hands the pro's own session across in the URL fragment (never a query string — fragments
 * don't reach servers or logs) and opens the studio full-page, because content creation is
 * a desktop activity (his words) and a workspace deserves the whole screen.
 *
 * The earlier hub-built flow (PROHUB-41/44/45) was becoming designer #2 — "chasing two
 * things all the time" — and was deleted on his call the same morning it shipped.
 *
 * VIEW-AS keeps a READ-ONLY lens here: an owner walking a pro's hub sees the pro's pieces
 * and can open them to look — the review need view-as exists for. It composes nothing; the
 * door refuses composing acts for viewers server-side either way.
 */

// CELL (M28): the Owner Hub's origin for this build — production's OWNER_HUB_ORIGIN (lib/hosts.ts), or the AWS staging
// host on the cell build; null there = not on the AWS staging build yet, and every door below says so (CELL_DOOR_OFF).
const OWNER_HUB_ORIGIN = ownerHubOrigin();
const DOOR = `${OWNER_HUB_ORIGIN}/pro/designer`;
const STUDIO = `${OWNER_HUB_ORIGIN}/pro/studio`;

interface Brief { id: string; brief: string; heading: string; updatedAt: string }

// HUB-025: the sign-in broker lives in src/lib/designer-token-broker.ts so the app starts it at boot (main.tsx) —
// a reloaded hub tab still renews its studio window's sign-in. Re-exported here for the callers that import it from here.
export { DESIGNER_TOKEN_REPLY, DESIGNER_TOKEN_REQUEST, makeDesignerTokenBroker } from '@/lib/designer-token-broker';

async function door(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (!OWNER_HUB_ORIGIN) return { ok: false, error: CELL_DOOR_OFF };
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const at = await ownerHubToken();
  if (!at) return { ok: false, error: 'Sign in again to use the Designer.' };
  const r = await fetch(DOOR, {
    method: 'POST',
    headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return ((await r.json().catch(() => null)) as Record<string, unknown> | null)
    ?? { ok: false, error: 'The Designer is unreachable right now — try again in a minute.' };
}

/** The read-only piece view for view-as — scaled like the studio renders it. */
function PieceFrame({ html }: { html: string }) {
  const box = useRef<HTMLDivElement | null>(null);
  const frame = useRef<HTMLIFrameElement | null>(null);
  const fit = () => {
    const f = frame.current;
    const d = f?.contentDocument;
    const bw = box.current?.clientWidth ?? 0;
    const bh = box.current?.clientHeight ?? 0;
    if (!f || !d?.documentElement || !bw) return;
    const w = Math.max(d.documentElement.scrollWidth, d.body?.scrollWidth ?? 0) || 816;
    const scale = bw / w;
    f.style.width = `${w}px`;
    f.style.height = `${bh / scale}px`;
    f.style.transform = `scale(${scale})`;
  };
  return (
    <div ref={box} className="rounded-xl overflow-hidden ring-1 ring-image-border bg-white"
      style={{ aspectRatio: '8.5 / 11' }} data-designer-piece>
      <iframe ref={frame} title="Their piece" srcDoc={html} sandbox="allow-same-origin"
        referrerPolicy="no-referrer" onLoad={fit} tabIndex={-1}
        className="border-0" style={{ transformOrigin: 'top left' }} />
    </div>
  );
}

export function ProDesigner({ locationId }: { locationId: string | null }) {
  const viewing = viewAsProId();
  const [note, setNote] = useState<string | null>(null);
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [openHtml, setOpenHtml] = useState<string | null>(null);
  /** An image-door piece (the door answers imageUrl, not html) — shown as the picture it is. */
  const [openImage, setOpenImage] = useState<string | null>(null);

  // The view-as review lens loads the pro's pieces; the pro's own list lives in the studio.
  useEffect(() => {
    let live = true;
    (async () => {
      if (!locationId || !viewing) return;
      const j = await door({ location_id: locationId, act: 'list', view_pro: viewing });
      if (live && j.ok && Array.isArray(j.briefs)) setBriefs(j.briefs as Brief[]);
    })();
    return () => { live = false; };
  }, [locationId, viewing]);

  if (!locationId) return null;

  /** The handoff: their own session, in the FRAGMENT, straight into the one surface. */
  const openStudio = async () => {
    setNote(null);
    if (!OWNER_HUB_ORIGIN) { setNote(CELL_DOOR_OFF); return; }
    // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
    const at = await ownerHubToken();
    if (!at) { setNote('Sign in again to open the Designer.'); return; }
    const w = window.open(`${STUDIO}#at=${encodeURIComponent(at)}&loc=${encodeURIComponent(locationId)}`, '_blank');
    // HUB-025: remember the window, so its sign-in can be renewed when it runs out (above).
    if (w) { openedStudios.add(w); listenForDesignerTokenRequests(); }
  };

  const openPiece = async (id: string) => {
    const j = await door({ location_id: locationId, act: 'resume', sessionId: id, view_pro: viewing ?? undefined });
    if (!j.ok) { setNote(String(j.error ?? 'That piece would not open.')); return; }
    // The door answers ONE of two shapes (image-os-web resumeView): a sheet as html, or an
    // image-door piece as imageUrl. Before this, an image piece read as "would not open".
    if (typeof j.imageUrl === 'string' && j.imageUrl) { setOpenHtml(null); setOpenImage(j.imageUrl); return; }
    if (typeof j.html !== 'string') { setNote('That piece would not open.'); return; }
    setOpenImage(null);
    setOpenHtml(j.html);
  };

  return (
    <section className="card border-l-4 border-l-image-pink" data-pro-designer>
      <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">The Designer</div>
      <h2 className="font-display font-extrabold text-lg mt-0.5">Your design studio.</h2>
      {note && <p className="text-xs text-image-charcoal mt-1" data-designer-note>{note}</p>}

      {!viewing && (
        <div className="mt-2 space-y-3">
          <p className="text-xs text-image-charcoal">
            Say what you need — a coupon, an offer, a price list — and it interviews you, then
            hands you the finished piece with your QR code on it. Tap anything to change it,
            try every look, download and print. Best on a bigger screen; posting is always yours.
          </p>
          <button type="button" onClick={openStudio}
            className="rounded-full px-4 py-2 text-sm font-bold bg-image-pink text-white hover:opacity-90 transition-all"
            data-designer-open>
            Open the Designer
          </button>
          <p className="text-[11px] text-image-charcoal">
            It opens in its own tab — your pieces are saved there under Past briefs.
          </p>
        </div>
      )}

      {viewing && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-image-charcoal">
            View-as is read only — their pieces below; composing stays theirs.
          </p>
          {openHtml && (
            <>
              <PieceFrame html={openHtml} />
              <button type="button" className="text-[11px] font-bold text-image-charcoal underline"
                onClick={() => setOpenHtml(null)}>Close</button>
            </>
          )}
          {openImage && (
            <>
              <div className="rounded-xl overflow-hidden ring-1 ring-image-border bg-white" data-designer-piece-image>
                <img src={openImage} alt="Their piece" className="block w-full h-auto" />
              </div>
              <button type="button" className="text-[11px] font-bold text-image-charcoal underline"
                onClick={() => setOpenImage(null)}>Close</button>
            </>
          )}
          {!openHtml && !openImage && briefs.map((b) => (
            <button key={b.id} type="button" onClick={() => openPiece(b.id)}
              className="w-full min-w-0 text-left rounded-xl ring-1 ring-image-border bg-white px-3 py-2 hover:ring-image-pink transition-all">
              <div className="text-sm font-bold text-image-black truncate">{b.heading || b.brief.slice(0, 60)}</div>
              <div className="text-[11px] text-image-charcoal truncate">{b.brief}</div>
            </button>
          ))}
          {!openHtml && !openImage && briefs.length === 0 && (
            <p className="text-xs text-image-charcoal">No pieces yet.</p>
          )}
        </div>
      )}
    </section>
  );
}
